import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { LINKEDIN_FEED_REPORT } from "../../../constants";
import { formatDateUS, formatTime } from "../../../utils/commonFn";
import { GET_LINKEDIN_FEED_REPORT } from "../gql";
import Listing, { LinkedinFeedReportRow } from "./Listing";

interface LinkedinFeedReportPayload {
  rows: LinkedinFeedReportRow[];
  totals: {
    companies: number;
    jobOrders: number;
    jobAds: number;
    totalJobOrders: number;
    totalJobAds: number;
  };
  generatedAt: string;
}

const LinkedinFeedReport = () => {
  const { data, loading, error } = useQuery<{ linkedinFeedReport: LinkedinFeedReportPayload }>(
    GET_LINKEDIN_FEED_REPORT,
    { fetchPolicy: "network-only" }
  );

  // The Apollo client runs with errorPolicy 'all', so failures surface on
  // `error` (and possibly partial/null data) rather than as thrown errors.
  const report = data?.linkedinFeedReport;
  const failed = !loading && (!!error || !report);

  useEffect(() => {
    document.title = `${LINKEDIN_FEED_REPORT} - RealREPP`;
  }, []);

  useEffect(() => {
    if (!loading && error) {
      toast.error(error.message || "Failed to load the LinkedIn feed report");
    }
  }, [loading, error]);

  return (
    <div className="admin-tabs team-tabs">
      <div className="primaryHeading">
        <h5>{LINKEDIN_FEED_REPORT}</h5>
      </div>

      {report && !loading && (
        <div className="primaryHeaderSpacing">
          <div className="lfr-summary">
            <div className="lfr-summary__tile">
              <span className="lfr-summary__label">Companies</span>
              <span className="lfr-summary__value">{report.totals.companies}</span>
            </div>
            <div className="lfr-summary__tile">
              <span className="lfr-summary__label">Job Orders</span>
              <span className="lfr-summary__value">{report.totals.jobOrders}</span>
            </div>
            <div className="lfr-summary__tile">
              <span className="lfr-summary__label">Job Ads</span>
              <span className="lfr-summary__value">
                {report.totals.jobAds}
                <small className="text-muted font-weight-normal"> / 165</small>
              </span>
            </div>
            <div className="lfr-summary__tile">
              <span className="lfr-summary__label">Total Active Job Orders</span>
              <span className="lfr-summary__value">{report.totals.totalJobOrders}</span>
            </div>
            <div className="lfr-summary__tile">
              <span className="lfr-summary__label">Total Job Ads</span>
              <span className="lfr-summary__value">{report.totals.totalJobAds}</span>
            </div>
          </div>
          <div className="lfr-note">
            <span>
              Top 165 ads, ordered the same way as the LinkedIn XML feed: company
              suppression, feed priority, highest pay, then newest. Live data;
              LinkedIn reads a snapshot refreshed when the feed job runs.
            </span>
            <span>
              Total Active Job Orders / Total Job Ads count every active job order of
              the listed companies with at least one posted location, and all of those
              posted locations, regardless of the 165 cap or the Post to LinkedIn flag.
            </span>
            {report.generatedAt && (
              <span>
                Generated {formatDateUS(report.generatedAt)} {formatTime(report.generatedAt)}
              </span>
            )}
          </div>
        </div>
      )}

      <Listing loading={loading} rows={failed ? [] : report?.rows ?? []} />
    </div>
  );
};

export default LinkedinFeedReport;
